import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  getDocs
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Custom hook for real-time Firestore queries
 * @param {string} collectionName - Firestore collection name
 * @param {Array} queryConstraints - Array of Firestore query constraints
 * @param {Object} options - Additional options { realtime: boolean, dependencies: array }
 * @returns {Object} { data, loading, error }
 */
export const useFirestore = (collectionName, queryConstraints = [], options = {}) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { realtime = true, dependencies = [] } = options;

  useEffect(() => {
    if (!collectionName) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const collectionRef = collection(db, collectionName);
      const q = queryConstraints.length > 0
        ? query(collectionRef, ...queryConstraints)
        : collectionRef;

      if (realtime) {
        // Real-time listener
        const unsubscribe = onSnapshot(
          q,
          (snapshot) => {
            const documents = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setData(documents);
            setLoading(false);
          },
          (err) => {
            console.error(`Error fetching ${collectionName}:`, err);
            setError(err.message);
            setLoading(false);
          }
        );

        return () => unsubscribe();
      } else {
        // One-time fetch
        getDocs(q)
          .then((snapshot) => {
            const documents = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setData(documents);
            setLoading(false);
          })
          .catch((err) => {
            console.error(`Error fetching ${collectionName}:`, err);
            setError(err.message);
            setLoading(false);
          });
      }
    } catch (err) {
      console.error(`Error setting up query for ${collectionName}:`, err);
      setError(err.message);
      setLoading(false);
    }
  }, [collectionName, ...dependencies]);

  return { data, loading, error };
};

/**
 * Custom hook for fetching trainer's clients with real-time updates
 * @param {string} trainerId - Trainer's user ID
 * @returns {Object} { clients, loading, error }
 */
export const useTrainerClients = (trainerId) => {
  const queryConstraints = trainerId ? [
    where('role', '==', 'client'),
    where('trainerId', '==', trainerId),
    where('isActive', '==', true)
  ] : [];

  return useFirestore('users', queryConstraints, {
    realtime: true,
    dependencies: [trainerId]
  });
};

/**
 * Custom hook for fetching client profiles with detailed info
 * @param {Array<string>} clientIds - Array of client IDs
 * @returns {Object} { profiles, loading, error }
 */
export const useClientProfiles = (clientIds) => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientIds || clientIds.length === 0) {
      setProfiles([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    const unsubscribers = [];

    clientIds.forEach(clientId => {
      const q = query(collection(db, 'clientProfiles'), where('userId', '==', clientId));
      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setProfiles(prev => {
            const filtered = prev.filter(p => p.userId !== clientId);
            return [...filtered, ...docs];
          });
        },
        (err) => {
          console.error('Error fetching client profile:', err);
          setError(err.message);
        }
      );
      unsubscribers.push(unsubscribe);
    });

    setLoading(false);

    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [JSON.stringify(clientIds)]);

  return { profiles, loading, error };
};

/**
 * Custom hook for fetching recent progress entries
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of entries to fetch
 * @returns {Object} { progress, loading, error }
 */
export const useProgressData = (userId, limitCount = 50) => {
  const queryConstraints = userId ? [
    where('userId', '==', userId),
    orderBy('date', 'desc'),
    limit(limitCount)
  ] : [];

  return useFirestore('progressEntries', queryConstraints, {
    realtime: false, // Use one-time fetch to avoid conflicts
    dependencies: [userId, limitCount]
  });
};

/**
 * Custom hook for fetching meal logs within date range
 * @param {string} userId - User ID
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {Object} { meals, loading, error }
 */
export const useMealLogs = (userId, startDate, endDate) => {
  const queryConstraints = userId && startDate && endDate ? [
    where('userId', '==', userId),
    where('date', '>=', startDate),
    where('date', '<=', endDate),
    orderBy('date', 'desc')
  ] : [];

  return useFirestore('mealLogs', queryConstraints, {
    realtime: true,
    dependencies: [userId, startDate?.getTime(), endDate?.getTime()]
  });
};

/**
 * Custom hook for fetching feedback for a trainer
 * @param {string} trainerId - Trainer ID
 * @returns {Object} { feedback, loading, error }
 */
export const useClientFeedback = (trainerId) => {
  const queryConstraints = trainerId ? [
    where('trainerId', '==', trainerId),
    orderBy('createdAt', 'desc'),
    limit(50)
  ] : [];

  return useFirestore('clientFeedback', queryConstraints, {
    realtime: true,
    dependencies: [trainerId]
  });
};

/**
 * Custom hook for fetching messages between trainer and client
 * @param {string} trainerId - Trainer ID
 * @param {string} clientId - Client ID (optional, for specific conversation)
 * @returns {Object} { messages, loading, error }
 */
export const useMessages = (trainerId, clientId = null) => {
  const queryConstraints = [];

  if (trainerId) {
    queryConstraints.push(where('trainerId', '==', trainerId));
  }

  if (clientId) {
    queryConstraints.push(where('clientId', '==', clientId));
  }

  if (queryConstraints.length > 0) {
    queryConstraints.push(orderBy('createdAt', 'desc'));
    queryConstraints.push(limit(100));
  }

  return useFirestore('communications', queryConstraints, {
    realtime: true,
    dependencies: [trainerId, clientId]
  });
};

/**
 * Custom hook for fetching workout programs
 * @param {string} trainerId - Trainer ID
 * @returns {Object} { programs, loading, error }
 */
export const useWorkoutPrograms = (trainerId) => {
  const queryConstraints = trainerId ? [
    where('trainerId', '==', trainerId),
    orderBy('createdAt', 'desc')
  ] : [];

  return useFirestore('workoutPrograms', queryConstraints, {
    realtime: true,
    dependencies: [trainerId]
  });
};

/**
 * Custom hook for fetching workout logs
 * @param {string} userId - User ID
 * @param {number} limitCount - Number of logs to fetch
 * @returns {Object} { workouts, loading, error }
 */
export const useWorkoutLogs = (userId, limitCount = 30) => {
  const queryConstraints = userId ? [
    where('userId', '==', userId),
    orderBy('date', 'desc'),
    limit(limitCount)
  ] : [];

  return useFirestore('workoutLogs', queryConstraints, {
    realtime: true,
    dependencies: [userId, limitCount]
  });
};

export default useFirestore;
